import React, { useEffect, useRef } from 'react';
import styles from './FloatingLabelTextArea.module.css';
import { useUnsavedChangesWarning } from '../../../../Hooks/useUnsavedChangesWarning';

type FloatingLabelTextAreaProps = {
  placeholder?: string;
  label: string;
  required?: boolean;
  onChange?: (value: string) => void;
  internalValueChanged?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  value: string | undefined; // Controlled value
  style?: React.CSSProperties; // Optional style prop
  readonly?: boolean; // Optional prop to make the textarea read-only
  suspended?: boolean; // Optional prop to indicate if the textarea is suspended
  minHeight?: number | string; // Optional minimum height
  height?: number | string; // Optional height
  useInternalValue?: boolean; // Whether to use internal state for value
};

const FloatingLabelTextArea = ({
  placeholder = '',
  label,
  required = false,
  onChange,
  internalValueChanged,
  value, // Controlled by props
  onBlur,
  onFocus,
  style,
  height,
  minHeight,
  readonly,
  suspended,
  useInternalValue = false
}: FloatingLabelTextAreaProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [internalValue, setInternalValue] = React.useState<string>(value ?? '');
  const [hasUserEdited, setHasUserEdited] = React.useState(false);

  useEffect(() => {
    setInternalValue(value ?? '');
    setHasUserEdited(false);
  }, [value]);

  useUnsavedChangesWarning(
    !readonly &&
      !suspended &&
      useInternalValue &&
      hasUserEdited &&
      internalValue !== (value ?? '')
  );

  // Auto-resize function
const adjustHeight = () => {
    const ta = textareaRef.current;
    if (!ta) return;

    // reset to measure correct scrollHeight
    ta.style.height = 'auto';

    const scrollHeight = ta.scrollHeight;

    if (typeof minHeight === 'number') {
      const heightPx = Math.max(scrollHeight, minHeight);
      ta.style.height = `${heightPx}px`;
    } else {
      // For string minHeight (e.g. "6rem" / "120px"), set height to content height;
      // browser will ensure min-height if it's larger than content height.
      ta.style.height = `${scrollHeight}px`;
    }

    if(height) {
      if (typeof height === 'number') {
        ta.style.height = `${height}px`;
      } else if (typeof height === 'string' && height.length > 0) {
        ta.style.height = height;
      }
    }
  };

    // Ensure minHeight style is applied and adjust height on mount / minHeight change
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;

    if (typeof minHeight === 'number') {
      ta.style.minHeight = `${minHeight}px`;
    } else if (typeof minHeight === 'string' && minHeight.length > 0) {
      ta.style.minHeight = minHeight;
    } else {
      ta.style.minHeight = '';
    }

    // adjust after applying minHeight
    adjustHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minHeight]);

  // Adjust height when value changes
  useEffect(() => {
    adjustHeight();
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if(suspended) return; // Do nothing if suspended
    const next = e.target.value;
    setHasUserEdited(true);
    setInternalValue(next);
    internalValueChanged?.(next);
    if (!useInternalValue) {
      onChange?.(next);
    }
  };

  // Handle input for real-time resizing
  const handleInput = () => {
    adjustHeight();
  };

  const internalBlurHandler = () => {
    if(suspended) return; // Do nothing if suspended
    if(useInternalValue) {
      onChange?.(internalValue);
    }
    onBlur?.();
  }

  return (
      <div className={styles.container}>
        <div className={styles.label}>
          {label}
          {required && <span className={styles.asterisk}>*</span>}
        </div>
        <textarea
          ref={textareaRef}
          className={`${styles.textarea} ${suspended ? styles.suspended : ''}`}
          placeholder={placeholder}
          value={internalValue} // Display value from parent
          onChange={handleChange} // Pass changes to the parent
          onInput={handleInput} // Handle real-time resizing
          required={required}
          onBlur={internalBlurHandler}
          onFocus={onFocus}
          style={style} // Apply optional styles
          disabled={readonly} // Make textarea read-only if specified
        />
      </div>
  );
};

export default FloatingLabelTextArea;
